import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import {
  SsnUploadPreview,
  usePresignMajorJobSsnUploadMutation,
  usePreviewMajorJobSsnUploadMutation,
  useReplaceMajorJobSsnFileMutation,
} from "features/adminUsers/adminUsers";

/** What the operator may send: the file a bot writes, or the sheet a client did. */
const ACCEPT = ".json,.csv,.xls,.xlsx";

/** The defects the SSN viewer names, in the words it uses. */
const DEFECT_LABELS: Array<[string, string]> = [
  ["several_names", "Several names"],
  ["no_name", "No name"],
  ["no_id", "No id"],
  ["invalid_ssn", "Not an SSN"],
  ["shared_id", "Shared name + id"],
];

/** How many left-out rows are listed before the rest are counted. */
const LEFT_OUT_SHOWN = 20;

function errorText(error: unknown, fallback: string): string {
  const data = (error as { data?: Record<string, unknown> })?.data;
  const said = data?.message ?? data?.detail;
  return typeof said === "string" && said ? said : fallback;
}

/**
 * Sending an SSN file by hand (CAL-6159).
 *
 * Three steps on purpose: the file goes to a staging key, the server says what
 * it would become, and only then does the operator replace the major job's
 * file. Nothing is written until Replace - the preview reads the staged bytes
 * and leaves them where they are, so Replace acts on the very file the numbers
 * were counted from.
 */
export default function SsnFileUploadDialog({
  open,
  majorJobId,
  majorJobName,
  onClose,
  onReplaced,
}: {
  open: boolean;
  majorJobId: number;
  majorJobName: string;
  onClose: () => void;
  /** The file changed: the tab, the header tile and readiness are re-read. */
  onReplaced: () => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");
  const [stagingKey, setStagingKey] = useState("");
  const [preview, setPreview] = useState<SsnUploadPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [presign] = usePresignMajorJobSsnUploadMutation();
  const [askPreview] = usePreviewMajorJobSsnUploadMutation();
  const [replace] = useReplaceMajorJobSsnFileMutation();

  const reset = useCallback(() => {
    setFileName("");
    setStagingKey("");
    setPreview(null);
    setError(null);
    setDone(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setDone(null);
    setPreview(null);
    setFileName(file.name);
    try {
      const slot = await presign({
        majorJobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }).unwrap();
      if (slot.max_bytes && file.size > slot.max_bytes) {
        throw new Error(
          `The upload is ${(file.size / (1024 * 1024)).toFixed(1)} MB and the limit is ` +
            `${Math.round(slot.max_bytes / (1024 * 1024))} MB.` +
            (slot.limit_note ? ` ${slot.limit_note}` : ""),
        );
      }
      const put = await fetch(slot.upload_url, {
        method: "PUT",
        headers: slot.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`The upload was refused (${put.status}).`);
      setStagingKey(slot.key);
      setPreview(await askPreview({ majorJobId, staging_key: slot.key }).unwrap());
    } catch (e) {
      setError(
        e instanceof Error && e.message.startsWith("The upload")
          ? e.message
          : errorText(e, "The file could not be read."),
      );
      setStagingKey("");
    } finally {
      setBusy(false);
    }
  };

  const onReplace = async () => {
    if (!stagingKey) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await replace({ majorJobId, staging_key: stagingKey }).unwrap();
      setDone(
        `The SSN file of '${majorJobName}' now holds ${answer.counts.ssns} SSN(s).`,
      );
      setStagingKey("");
      await onReplaced();
    } catch (e) {
      setError(errorText(e, "The SSN file was not replaced."));
      // The server keeps the staged file until a write succeeds, so a busy
      // job (409) or a storage error (5xx) is retried on the same upload.
      // Anything else is about the file itself: the operator picks again.
      const code = (e as { status?: number })?.status;
      if (!(code === 409 || (typeof code === "number" && code >= 500))) {
        setStagingKey("");
      }
    } finally {
      setBusy(false);
    }
  };

  const counts = preview?.counts ?? {};
  const defects = DEFECT_LABELS.filter(([key]) => Number(counts[key] ?? 0) > 0);
  const blocked = Boolean(preview?.busy) || Boolean(preview?.holds_nobody);

  return (
    <AppDialog
      open={open}
      onClose={close}
      maxWidth="md"
      title={<>Upload SSN file - {majorJobName}</>}
      actions={
        <>
          <Button onClick={close}>Close</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || !stagingKey || !preview || blocked}
            onClick={() => void onReplace()}
          >
            {preview?.replaces_existing ? "Replace the SSN file" : "Save the SSN file"}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        The SSN file an SSN bot writes (ssn_info.json), or a spreadsheet with the
        columns SSN, Employee name and Employee id. It becomes this major job's own
        SSN file; no other major job's file changes.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          Choose file
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            const picked = e.target.files?.[0];
            // Cleared straight away, or picking the same file again - which
            // this dialog itself suggests after a fix - fires no change.
            e.target.value = "";
            void onPick(picked);
          }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {fileName || "json, csv, xls or xlsx"}
        </Typography>
        {busy ? <CircularProgress size={16} /> : null}
      </Stack>
      {busy ? <LinearProgress sx={{ mb: 1 }} /> : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}
      {done ? (
        <Alert severity="success" sx={{ mb: 1 }}>
          {done}
        </Alert>
      ) : null}

      {preview ? (
        <Box>
          {preview.busy ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {preview.busy.message}
            </Alert>
          ) : null}
          {preview.holds_nobody ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              Every row of this file was left out, so it would hold nobody. Fix the
              file, or delete the SSN file if that is what you mean.
            </Alert>
          ) : null}

          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            <Chip size="small" label={`${counts.ssns ?? 0} SSN(s)`} color="primary" />
            <Chip size="small" variant="outlined" label={`${counts.entries ?? 0} employee record(s)`} />
            <Chip size="small" variant="outlined" label={`${counts.names ?? 0} name(s)`} />
            <Chip size="small" variant="outlined" label={`${preview.rows_read} row(s) read`} />
            {preview.left_out_total > 0 ? (
              <Chip
                size="small"
                color="warning"
                label={`${preview.left_out_total} left out`}
              />
            ) : null}
          </Stack>

          {defects.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {defects.map(([key, label]) => (
                <Chip key={key} size="small" color="warning" variant="outlined" label={`${label}: ${counts[key]}`} />
              ))}
            </Stack>
          ) : null}

          {preview.readers?.length ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              {`This file is read by: ${preview.readers
                .map((job) => (job.own ? job.name : `${job.name} (another major job)`))
                .join(", ")}.`}
            </Alert>
          ) : null}

          {preview.left_out.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Left out - these rows are not written
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Where</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Why</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.left_out.slice(0, LEFT_OUT_SHOWN).map((row, index) => (
                    <TableRow key={`${row.where}-${index}`}>
                      <TableCell>{row.where}</TableCell>
                      <TableCell>{row.value || "(blank)"}</TableCell>
                      <TableCell>
                        {[row.employee_name, row.employee_id].filter(Boolean).join(" / ") || "-"}
                      </TableCell>
                      <TableCell>{row.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.left_out_total > LEFT_OUT_SHOWN ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {`Showing ${Math.min(
                    LEFT_OUT_SHOWN,
                    preview.left_out.length,
                  )} of ${preview.left_out_total} - fix the file and upload it again to see the rest.`}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </AppDialog>
  );
}
